import { useEffect, useSyncExternalStore } from 'react';
import { useIdentity } from './useIdentity';
import { GLOB } from '../router/paths';
import {
  subscribe,
  getSnapshot,
  ensureLoaded,
} from '../features/nav/roomHistoryStore';

/**
 * useRoomHistory — the visitor's "rooms I've interacted in" list.
 *
 * Backed by the session-scoped roomHistoryStore: the underlying listMyRooms
 * query fires AT MOST ONCE per tab session (the store caches it); both the
 * desktop sidebar and the mobile sheet share that single fetch and re-render
 * live when a new message records activity (recordRoomActivity).
 *
 * Returns `rooms` (RoomSummary[]) sorted by most-recent activity, with #GLOB
 * filtered out — GLOB is always pinned by the consuming UI, separate from the
 * "your parties" list. `loading` is true until the first fetch resolves and we
 * have no cached/optimistic rows to show yet.
 *
 * @returns {{ rooms: Array<{code: string, lastActive: number|null}>, loading: boolean }}
 */
export function useRoomHistory() {
  const { user } = useIdentity();

  useEffect(() => {
    if (user) ensureLoaded(user);
  }, [user]);

  const all = useSyncExternalStore(
    subscribe,
    () => getSnapshot(user),
    () => getSnapshot(user),
  );

  const rooms = all
    .filter((r) => r.code && r.code !== GLOB)
    .sort((a, b) => (b.lastActive ?? 0) - (a.lastActive ?? 0));

  // We treat "no rows yet" as loading. Once a room is known (cached, hydrated,
  // or optimistically recorded) the list paints — even mid-fetch.
  const loading = user ? all.length === 0 : false;

  return { rooms, loading };
}

/**
 * Merge the current party into a room-history list so the room the visitor is
 * CURRENTLY in is always visible — even before they've sent a message there
 * (i.e. before it qualifies for the persisted history). Prepends it when
 * absent; leaves the list untouched in #GLOB (GLOB is pinned separately).
 *
 * @param {Array<{code: string, lastActive: number|null}>} rooms
 * @param {string} partyCode
 * @param {boolean} isInGlob
 */
export function mergeCurrentParty(rooms, partyCode, isInGlob) {
  if (isInGlob || !partyCode || partyCode === GLOB) return rooms;
  if (rooms.some((r) => r.code === partyCode)) return rooms;
  return [{ code: partyCode, lastActive: null }, ...rooms];
}

export default useRoomHistory;
